import store from '../../store';
import AudioTrackerTask from '../../requests/tasks/trackers/AudioTrackerTask';
import RequestManager from '../../requests/RequestManager';
import Utils from '../Utils';
import { AAF_CONTENT_IS_AUDIO } from '../../AAFPlayer/constants';
import logger from 'src/utils/logger/logger';

const DEFAULT_TICK_INTERVAL = 20;

class AudioTracker {
    static policies = {
        tickInterval: DEFAULT_TICK_INTERVAL
    };

    static urls = {
        completion: {
            url: null,
            params: null,
            method: 'POST',
            showModalError: true,
            body: null,
            idx_body: null
        },
        error: { url: null, params: null, method: 'GET', showModalError: true },
        interval: null,
        tick: { url: null, params: null, method: 'GET', showModalError: false }
    };

    static currentStreamContentType = null;

    static audioTracking = {
        timePlaying: {
            secondsDuration: 0, // seconds
            intervalId: null,
            intervalSeconds: 1 // segundo el interval
        }
    };

    static isAudio() {
        if (AudioTracker.currentStreamContentType === null) {
            return false;
        }

        let isAudioStream = false;
        switch (AudioTracker.currentStreamContentType) {
            case AAF_CONTENT_IS_AUDIO:
                isAudioStream = true;
                break;
            default:
                break;
        }

        return isAudioStream;
    }

    constructor() {
        this.tickIntervalId = null;
        this.tick = this.tick.bind(this);
    }

    resetTimePlaying() {
        AudioTracker.audioTracking.timePlaying.secondsDuration = 0;
        this.pauseTimePlaying();
    }

    startTimePlaying() {
        this.pauseTimePlaying(); // For security

        AudioTracker.audioTracking.timePlaying.intervalId = setInterval(() => {
            // console.log(
            //     '[REALTIME] AudioTrack time playing ' +
            //         AudioTracker.audioTracking.timePlaying.secondsDuration
            // );
            AudioTracker.audioTracking.timePlaying.secondsDuration += 1;
        }, AudioTracker.audioTracking.timePlaying.intervalSeconds * 1000);
    }

    pauseTimePlaying() {
        if (AudioTracker.audioTracking.timePlaying.intervalId) {
            clearInterval(AudioTracker.audioTracking.timePlaying.intervalId);
        }
    }

    getTimePlaying() {
        return AudioTracker.audioTracking.timePlaying.secondsDuration;
    }

    getTrackUrl(url) {
        if (!url) return undefined;
        const _url = Utils.getRequest(url);

        return _url;
    }

    setupAudioTrack(audioTrackInfo, extras = null, streamContentType = null) {
        // Verificar por audio
        const { audioTracking: { error = {}, success = {}, tick = {} } = {} } = audioTrackInfo;

        try {
            if (error && error.url) {
                const eUrl = this.getTrackUrl(error.url);
                AudioTracker.urls.error.url = eUrl.uri;
                AudioTracker.urls.error.params = eUrl.params;
            }

            if (success && success.url) {
                const sUrl = this.getTrackUrl(success.url);
                AudioTracker.urls.completion.url = sUrl.uri;

                const deviceParams = success.body;
                const deviceData = success.data;
                const requestData = audioTrackInfo.requestData;

                // Replace de params del device
                deviceParams.device.model = requestData.device_model;
                deviceParams.device.os_version = requestData.os_version;

                // Replace de params, el primero que encuentra, no tengo info de track & album & playlist
                let idx = null;
                let _done = false;
                for (let trackId_containerId in deviceData.phonograms) {
                    if (!_done) {
                        idx = trackId_containerId;
                        _done = true;
                    }
                }

                if (idx) {
                    deviceData.phonograms[idx].net_type = 1;
                    deviceData.phonograms[idx].event.is_starter = 0;
                    AudioTracker.urls.completion.idx_body = idx;
                }

                AudioTracker.urls.completion.body = { body: deviceParams, data: deviceData };
                sUrl.params.list_id = sUrl.params.playlist;
                delete sUrl.params.playlist;
                AudioTracker.urls.completion.params = sUrl.params;
            }

            if (tick && tick.url) {
                const tUrl = this.getTrackUrl(tick.url);
                AudioTracker.urls.tick.url = tUrl.uri;
                AudioTracker.urls.tick.params = tUrl.params;
            }

            if (tick.interval_timer) {
                AudioTracker.policies.tickInterval = tick.interval_timer;
            }
        } catch (err) {
            AudioTracker.policies.tickInterval = DEFAULT_TICK_INTERVAL;
        }

        // Reset stream anterior
        if (AudioTracker.currentStreamContentType !== null) {
            this.setCurrentStreamContentType(null);
        }

        // Update stream content type
        if (streamContentType !== null) {
            this.setCurrentStreamContentType(streamContentType);
        }
    }

    setCurrentStreamContentType(streamContentType) {
        AudioTracker.currentStreamContentType = streamContentType;
    }

    startTicking() {
        if (!AudioTracker.isAudio()) return;

        logger.debug('AudioTracker startTicking');
        if (this.tickIntervalId) {
            logger.debug('AudioTracker clear this.tickInterval');
            clearInterval(this.tickIntervalId);
        }
        const interval = AudioTracker.policies.tickInterval * 1000;

        this.tickIntervalId = setInterval(this.tick, interval);
    }

    stopTicking() {
        if (!AudioTracker.isAudio()) return;

        logger.debug('AudioTracker stopTicking');
        clearInterval(this.tickIntervalId);
    }

    tick(time) {
        if (!AudioTracker.isAudio()) return;

        if (isNaN(time) || time <= 0) {
            return;
        }
        logger.info('[======] AudioTracker sending tick as param ', time);
        this.track('tick', time);
    }

    error(e, time = 0) {
        if (!AudioTracker.isAudio()) return;
        logger.debug('AudioTracker error');
        this.track('error', time);
    }

    end(time = 0) {
        if (!AudioTracker.isAudio()) return;

        if (typeof AudioTracker.urls['completion'] !== 'undefined') {
            AudioTracker.urls.completion.body.data.phonograms[
                AudioTracker.urls.completion.idx_body
            ].duration = time;

            logger.debug('[======] AudioTracker end');
            const _data = { data: AudioTracker.urls.completion.body.data };
            const body = { ...AudioTracker.urls.completion.body.body, ..._data };

            this.track('completion', time, body);
        }
    }

    async track(trackingMethod = '', time = 0, body = null) {
        if (!AudioTracker.isAudio()) return;

        // Por el momento la url adicional se usa para el trackStop
        let urlData = AudioTracker.urls[trackingMethod];

        let url = urlData.url;
        let params = urlData.params;

        if (url) {
            const { user: { musicUser: { cm_access_token = null } = {} } = {} } = store.getState();
            logger.debug(`[======] AudioTracker request params: ${urlData.params}`);
            params.timecode = (time && time.toFixed(0)) || 0;
            if (url) {
                try {
                    const simpleGetRequestTask = new AudioTrackerTask(
                        url,
                        params,
                        urlData.showModalError,
                        cm_access_token,
                        urlData.method,
                        body
                    );
                    await RequestManager.addRequest(simpleGetRequestTask);
                } catch (err) {
                    logger.warn(`[======] AudioTracker request failed, url: ${url}`, err);
                }
            } else {
                logger.warn(`[======] No AudioTracker url found for method: ${method}`);
            }
        }
    }
}

export default AudioTracker;



// WEBPACK FOOTER //
// ./src/utils/trackers/AudioTracker.js